import React, { useState } from 'react';
import { SectionHeader } from '../components/UI';
import { useInView } from '../hooks/useInView';
import { loadRazorpayScript } from '../utils/razorpay';
import styles from './Pricing.module.css';

const FEATURES = [
  '8-week structured curriculum (Meta Ads + Google Ads)',
  'Live interactive sessions + full recordings',
  'Hands-on campaign exercises with real ad accounts',
  'All 4 bonus modules (Influencer, Email, AI, Case Studies)',
  'Verifiable certificate of completion',
  '6 months of post-course doubt support',
  'Career guidance & freelancing roadmap',
  'Lifetime access to updated course materials',
];

const API = process.env.REACT_APP_API_URL || '/api';

export default function Pricing() {
  const [ref, inView] = useInView();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);

  const handlePayment = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Load Razorpay checkout script
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Payment gateway unavailable. Check your connection and try again.');

      // 2. Ask server to create an order (key_secret never leaves server)
      const orderRes = await fetch(`${API}/create-order.php`, {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!orderRes.ok) throw new Error('Could not initiate payment. Please try again.');
      const { orderId, amount, currency, keyId } = await orderRes.json();

      // 3. Open Razorpay checkout — only public keyId used here
      const options = {
        key        : keyId,
        amount,
        currency,
        name       : 'Digimint',
        description: 'Performance Marketing Masterclass',
        image      : '/mentor.jpg',
        order_id   : orderId,
        handler    : async (response) => {
          // 4. Verify signature on server — prevents fake payment confirmations
          try {
            const verifyRes = await fetch(`${API}/verify-payment.php`, {
              method : 'POST',
              headers: { 'Content-Type': 'application/json' },
              body   : JSON.stringify(response),
            });
            const vData = await verifyRes.json();
            if (vData.verified) {
              setSuccess(true);
            } else {
              setError('Payment verification failed. Contact us with your Payment ID: ' + response.razorpay_payment_id);
            }
          } catch {
            setError('Verification error. Contact support with your Payment ID: ' + response.razorpay_payment_id);
          } finally {
            setLoading(false);
          }
        },
        prefill: { name: '', email: '', contact: '' },
        theme  : { color: '#e84c1e' },
        modal  : { ondismiss: () => setLoading(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setError('Payment failed: ' + (resp.error?.description || 'Please try again.'));
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <SectionHeader
          label="Investment"
          title="One transparent price. Everything included."
          subtitle="No upsells, no surprise fees, no hidden upgrades. What you see is what you pay."
          center
        />

        <div className={`${styles.card} ${inView ? styles.visible : ''}`} ref={ref}>
          <div className={styles.cardTop} />

          <div className={styles.topRow}>
            <div>
              <span className={styles.tag}>🎯 Performance Marketing Masterclass</span>
              <div className={styles.tagline}>Meta Ads + Google Ads + Bonus Modules</div>
            </div>
            <div className={styles.priceBlock}>
              <span className={styles.original}>₹24,999</span>
              <span className={styles.price}>₹9,999</span>
              <span className={styles.per}>one-time · all inclusive</span>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.featuresHeader}>What's included</div>
          <div className={styles.features}>
            {FEATURES.map((f, i) => (
              <div key={i} className={styles.feature}>
                <span className={styles.check}>✓</span>
                {f}
              </div>
            ))}
          </div>

          <div className={styles.divider} />

          {success ? (
            <div className={styles.paySuccess}>
              ✅ Payment successful! Enrollment details will be sent to your email shortly.
            </div>
          ) : (
            <>
              <button
                className={`${styles.cta} ${loading ? styles.ctaLoading : ''}`}
                onClick={handlePayment}
                disabled={loading}
              >
                {loading ? (
                  <><span className={styles.spinner} /> Processing…</>
                ) : (
                  <>Enroll Now — Pay ₹9,999 <span className={styles.arrow}>→</span></>
                )}
              </button>
              {error && <p className={styles.payError}>{error}</p>}
            </>
          )}

          <p className={styles.emi}>💳 EMI options available — ask us during counselling</p>

          <div className={styles.guarantee}>
            <span className={styles.guaranteeIcon}>🛡️</span>
            <div>
              <strong>2-day refund policy</strong> — Attend the first two sessions. If you feel
              this isn't the right fit, we'll refund your full payment with no questions asked.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
